// The Gantt itself: phase swimlanes down, time across. A planned bar, an
// actual-completion marker where one exists, a today line, and dependency
// arrows drawn from blocks_count and depends_on_ids.
//
// Every x position comes from planned_start / planned_end / actual_completion
// as v_dev_plan_gantt returns them. An item missing either planned date draws
// no bar at all and says so in words, in the track itself, not only in the
// label column: see computeChartDomain and the "no dates set" branch below.
// Nothing here computes variance, overdue, or dependency state; those are
// read straight off the row.
import { useMemo } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../lib/shadcn/tooltip'
import { Badge } from '../../lib/shadcn/badge'
import { cn } from '../../lib/shadcn/utils'
import {
  computeChartDomain,
  dateToX,
  domainWidthPx,
  formatDate,
  formatOwner,
  formatVariance,
  monthTicks,
  statusLabel,
  todayX,
  STATUS_BADGE_VARIANT,
  type GanttItem,
} from '../../utils/devPlan'

const ROW_HEIGHT = 30
const HEADER_HEIGHT = 28
const BAR_HEIGHT = 14
const LABEL_WIDTH = 260

type Row = { item: GanttItem; y: number }

export function GanttChart({ items }: { items: GanttItem[] }) {
  const domain = useMemo(() => computeChartDomain(items), [items])

  const rows = useMemo<Row[]>(() => {
    // Group by phase, ordered by phase_sort_order (nulls last), then by
    // planned_start (nulls last) then ref within a phase. A structural
    // grouping, kept stable regardless of how the detail table is sorted.
    const byPhase = new Map<string, GanttItem[]>()
    for (const i of items) {
      const list = byPhase.get(i.phase_key) ?? []
      list.push(i)
      byPhase.set(i.phase_key, list)
    }
    const phaseKeys = [...byPhase.keys()].sort((a, b) => {
      const pa = byPhase.get(a)![0]
      const pb = byPhase.get(b)![0]
      const sa = pa.phase_sort_order ?? Number.MAX_SAFE_INTEGER
      const sb = pb.phase_sort_order ?? Number.MAX_SAFE_INTEGER
      if (sa !== sb) return sa - sb
      return (pa.phase_label ?? pa.phase_key).localeCompare(pb.phase_label ?? pb.phase_key)
    })
    const out: Row[] = []
    let y = HEADER_HEIGHT
    for (const key of phaseKeys) {
      const list = byPhase.get(key)!
      list.sort((a, b) => {
        if (a.planned_start === null && b.planned_start === null) return a.ref.localeCompare(b.ref)
        if (a.planned_start === null) return 1
        if (b.planned_start === null) return -1
        if (a.planned_start !== b.planned_start) return a.planned_start < b.planned_start ? -1 : 1
        return a.ref.localeCompare(b.ref)
      })
      for (const item of list) {
        out.push({ item, y })
        y += ROW_HEIGHT
      }
    }
    return out
  }, [items])

  const rowByItemId = useMemo(() => {
    const m = new Map<string, Row>()
    for (const r of rows) m.set(r.item.item_id, r)
    return m
  }, [rows])

  const width = domainWidthPx(domain)
  const height = HEADER_HEIGHT + rows.length * ROW_HEIGHT
  const ticks = useMemo(() => monthTicks(domain), [domain])
  const today = todayX(domain)

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing matches the current filters.</p>
  }

  return (
    <div className="flex rounded-md border border-border">
      {/* Sticky label column: ref, title, status pill. Not part of the date
          axis, so it never scrolls with it. */}
      <div
        className="shrink-0 border-r border-border bg-card"
        style={{ width: LABEL_WIDTH }}
      >
        <div style={{ height: HEADER_HEIGHT }} className="border-b border-border" />
        {rows.map(({ item, y }) => (
          <Tooltip key={item.item_id}>
            <TooltipTrigger asChild>
              <div
                className="flex items-center gap-1.5 border-b border-border/50 px-2 text-xs"
                style={{ height: ROW_HEIGHT }}
              >
                <span className="shrink-0 font-medium tabular-nums">{item.ref}</span>
                <span className="truncate text-muted-foreground">{item.title}</span>
                <Badge variant={STATUS_BADGE_VARIANT[item.status] ?? 'outline'} className="ml-auto shrink-0">
                  {statusLabel(item.status)}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs text-xs">
              <p className="font-medium">
                {item.ref}: {item.title}
              </p>
              <p className="mt-1 text-muted-foreground">
                Planned {formatDate(item.planned_start)} to {formatDate(item.planned_end)}
              </p>
              <p className="text-muted-foreground">
                Actual completion {formatDate(item.actual_completion)} ({formatVariance(item.variance_days)})
              </p>
              <p className="text-muted-foreground">Owner: {formatOwner(item.owner_name)}</p>
              {item.blocked_reason && (
                <p className="text-muted-foreground">Blocked: {item.blocked_reason}</p>
              )}
              {item.dependency_state !== 'clear' && (
                <p className="text-muted-foreground">
                  Dependency: {item.dependency_state === 'waiting_on_decision' ? 'waiting on a decision' : 'waiting'}
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>

      {/* The chart itself: one horizontally scrolling SVG canvas. */}
      <div className="min-w-0 flex-1 overflow-x-auto">
        <svg width={width} height={height} role="img" aria-label="Development plan Gantt chart">
          {/* Month ticks */}
          {ticks.map((t) => (
            <g key={t.x}>
              <line x1={t.x} y1={0} x2={t.x} y2={height} stroke="currentColor" strokeOpacity={0.08} />
              <text x={t.x + 4} y={HEADER_HEIGHT - 8} fontSize={10} fill="currentColor" opacity={0.6}>
                {t.label}
              </text>
            </g>
          ))}

          {/* Row backgrounds and bars */}
          {rows.map(({ item, y }) => {
            const x1 = dateToX(item.planned_start, domain)
            const x2 = dateToX(item.planned_end, domain)
            const hasBar = x1 !== null && x2 !== null
            const actualX = dateToX(item.actual_completion, domain)
            const barY = y + (ROW_HEIGHT - BAR_HEIGHT) / 2

            let severity: 'overdue' | 'awaiting_decision' | null = null
            if (item.is_overdue) severity = 'overdue'
            else if (item.status === 'awaiting_decision') severity = 'awaiting_decision'

            return (
              <g key={item.item_id}>
                <line x1={0} y1={y + ROW_HEIGHT} x2={width} y2={y + ROW_HEIGHT} stroke="currentColor" strokeOpacity={0.06} />
                {hasBar ? (
                  <g>
                    <rect
                      x={x1}
                      y={barY}
                      width={Math.max(2, (x2 as number) - x1)}
                      height={BAR_HEIGHT}
                      rx={3}
                      className={cn(
                        severity === 'overdue' && 'fill-destructive/70',
                        severity === 'awaiting_decision' && 'fill-[hsl(var(--warning))]',
                        !severity && 'fill-primary/60',
                      )}
                    >
                      <title>
                        {item.ref}: {item.title} ({formatDate(item.planned_start)} to {formatDate(item.planned_end)})
                      </title>
                    </rect>
                    {severity && (
                      <rect x={x1} y={barY} width={3} height={BAR_HEIGHT} className={cn(severity === 'overdue' ? 'fill-destructive' : 'fill-[hsl(var(--warning-foreground))]')} />
                    )}
                  </g>
                ) : (
                  <text x={4} y={barY + BAR_HEIGHT - 3} fontSize={11} fill="currentColor" opacity={0.5}>
                    no dates set
                  </text>
                )}
                {actualX !== null && (
                  <g transform={`translate(${actualX}, ${y + ROW_HEIGHT / 2})`}>
                    <title>
                      Actual completion {formatDate(item.actual_completion)} ({formatVariance(item.variance_days)})
                    </title>
                    <path d="M0 -6 L6 0 L0 6 L-6 0 Z" className="fill-foreground" />
                  </g>
                )}
              </g>
            )
          })}

          {/* Dependency arrows, only between items visible in this filtered set
              and only where both ends have a real position. A dependency on an
              item filtered out, or with no dates, draws no arrow: a guessed
              line would be worse than no line. */}
          {rows.map(({ item }) => {
            const targetRow = rowByItemId.get(item.item_id)
            if (!targetRow) return null
            return item.depends_on_ids.map((depId) => {
              const sourceRow = rowByItemId.get(depId)
              if (!sourceRow) return null
              const x1 = dateToX(sourceRow.item.planned_end, domain)
              const x2 = dateToX(targetRow.item.planned_start, domain)
              if (x1 === null || x2 === null) return null
              const y1 = sourceRow.y + ROW_HEIGHT / 2
              const y2 = targetRow.y + ROW_HEIGHT / 2
              return (
                <g key={`${depId}-${item.item_id}`}>
                  <path
                    d={`M ${x1} ${y1} L ${x1} ${(y1 + y2) / 2} L ${x2} ${(y1 + y2) / 2} L ${x2} ${y2}`}
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity={0.35}
                    strokeWidth={1.5}
                  />
                  <path d={`M ${x2} ${y2} L ${x2 - 4} ${y2 - 4} L ${x2 - 4} ${y2 + 4} Z`} className="fill-current opacity-35" />
                </g>
              )
            })
          })}

          {/* Today line, drawn last so it sits above the bars. */}
          {today >= 0 && today <= width && (
            <line x1={today} y1={0} x2={today} y2={height} stroke="currentColor" strokeDasharray="3,3" strokeOpacity={0.6} />
          )}
        </svg>
      </div>
    </div>
  )
}
